// frontend/src/components/common/StatusFlowPanel.js
// The status flow from the spec, driven by the server's allowed transitions.
// Works for both parts and tickets.
//
//   Yet to Start -> In Progress -> Peer QC -> Final QC
//     -> Sent for Customer Approval -> PLM Approval -> Completed
//
// At a QC / approval gate: Approve advances, Reject sends it back to
// In Progress as rework and bumps the loop counter.
// On Hold is reachable from any live stage and requires a comment.
import React, { useEffect, useState } from 'react';
import {
    Box, Stepper, Step, StepLabel, Typography, Button, TextField, Chip,
    Alert, CircularProgress, Divider, Stack, MenuItem, List, ListItem,
    ListItemText, Tooltip,
} from '@mui/material';
import {
    CheckCircle, ThumbUp, ThumbDown, PauseCircle, ArrowForward, History,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import useReferenceData from '../../hooks/useReferenceData';
import {
    MAIN_FLOW_STEPS, TICKET_MAIN_FLOW_STEPS, DECISION_STATUSES,
    OWNER_STATUSES, TICKET_OWNER_STATUSES, QC_STATUSES,
    SIMPLE_MAIN_FLOW_STEPS, SIMPLE_OWNER_STATUSES, isSimpleTaskType,
    statusChipColor, fmtDate, fmtTatDays,
} from '../../constants/domain';
import { parseHours } from '../parts/PartForm';

// Only these two "Move to" targets ask for a QC Engineer -- tickets never
// have this status in their flow at all (isTicket already excludes them).
const QC_GATE_TARGETS = ['Internal - Peer QC', 'Internal - Final QC'];

// At a gate, Approve/Feedback replace the raw "next stage" / "In Progress"
// entries in the same "Move to" dropdown -- one control instead of two ways
// to do the same thing. Values are sentinels (never real status names) so
// handleMove can tell them apart from a plain manual move.
const APPROVE_VALUE = '__approve__';
const REJECT_VALUE = '__reject__';

const StatusFlowPanel = ({
    item,                 // the part or ticket
    label = 'Part',       // 'Part' | 'Ticket'
    onSetStatus,          // (body) => Promise
    onDecide,             // (body) => Promise
    onLoadHistory,        // () => Promise<rows>
    onLoadLinkedParts,    // (tickets only) () => Promise<rows> -- parts created off this ticket
    onDone,               // called after a successful change
    readOnly = false,      // view only -- hides every move/approve/reject control
}) => {
    const ref = useReferenceData();

    const [comment, setComment] = useState('');
    const [target, setTarget] = useState('');
    const [qcEngineer, setQcEngineer] = useState('');
    const [stageHours, setStageHours] = useState('');
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [linkedParts, setLinkedParts] = useState([]);

    const isTicket = label === 'Ticket';
    // Training / Documentation / Meeting use the short workflow
    // (Yet to Start -> In Progress -> Validation -> Completed).
    const simple = isSimpleTaskType(item?.task_type);
    const mainFlowSteps = simple ? SIMPLE_MAIN_FLOW_STEPS
        : isTicket ? TICKET_MAIN_FLOW_STEPS : MAIN_FLOW_STEPS;
    const ownerStatuses = simple ? SIMPLE_OWNER_STATUSES
        : isTicket ? TICKET_OWNER_STATUSES : OWNER_STATUSES;

    const current = item?.status;
    const allowed = item?.allowed_next || [];
    // Tickets never sit in Internal - Peer/Final QC (removed from their flow),
    // so this naturally only fires for Sent for Customer Approval / PLM
    // Approval on a ticket -- both still approve/reject-able for tickets too.
    const isGate = DECISION_STATUSES.includes(current);

    // Hours are entered manually at every stage, never auto-calculated --
    // the one exception is leaving "Yet to Start" for anything other than
    // In Progress (On Hold / Cancelled): no work has happened yet, so there's
    // nothing to log. A gate decision (Approve/Reject) always asks, since a
    // QC gate is never "Yet to Start". Whatever's typed is added to the
    // running actual_hours total.
    const requiresHoursForMove = !!target && !(current === 'Yet to Start' && target !== 'In Progress');
    // Actual Hours is always visible while the work is In Progress.
    const showHoursField = isGate || requiresHoursForMove || current === 'In Progress';

    // Stage-based edit lock: only the party currently responsible (owner or
    // QC) may act. The server enforces this too -- can_act_on_stage just
    // drives whether we show the controls at all.
    const locked = item && item.can_act_on_stage === false;
    const responsible = ownerStatuses.includes(current) ? 'owner'
        : (!isTicket && !simple && QC_STATUSES.includes(current)) ? 'qc' : null;
    const responsibleName = responsible === 'owner' ? item?.assigned_to_name
        : responsible === 'qc' ? item?.qc_engineer_name : null;

    const loadHistory = React.useCallback(() => {
        if (!onLoadHistory) return;
        onLoadHistory().then(setHistory).catch(() => setHistory([]));
    }, [onLoadHistory]);

    const loadLinkedParts = React.useCallback(() => {
        if (!onLoadLinkedParts) return;
        onLoadLinkedParts().then(setLinkedParts).catch(() => setLinkedParts([]));
    }, [onLoadLinkedParts]);

    useEffect(() => { loadHistory(); }, [loadHistory]);
    useEffect(() => { loadLinkedParts(); }, [loadLinkedParts]);
    useEffect(() => {
        setTarget('');
        setComment('');
        setQcEngineer(item?.qc_engineer || '');
        setStageHours('');
    }, [item?.id, current]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeStep = Math.max(0, mainFlowSteps.indexOf(current));
    // The status Approve would land on -- used to drop the raw "next stage"
    // and "In Progress" entries from the dropdown at a gate, since Approve /
    // Feedback (rework) already cover them.
    const approveTarget = mainFlowSteps[activeStep + 1];
    // Entering a QC gate -- either by picking it directly on a manual move,
    // or by approving into one from the stage before it -- always needs a
    // named QC engineer, not just an "Unassigned" placeholder.
    const showQcField = !isTicket && (
        QC_GATE_TARGETS.includes(target) ||
        (target === APPROVE_VALUE && QC_GATE_TARGETS.includes(approveTarget))
    );
    const moveOptions = isGate
        ? [
            { value: APPROVE_VALUE, label: 'Approve' },
            { value: REJECT_VALUE, label: 'Feedback / Rework' },
            ...allowed.filter(s => s !== 'In Progress' && s !== approveTarget)
                .map(s => ({ value: s, label: s })),
        ]
        : allowed.map(s => ({ value: s, label: s }));

    const run = async (fn, successMessage) => {
        setBusy(true);
        try {
            await fn();
            toast.success(successMessage);
            setComment('');
            setTarget('');
            setQcEngineer('');
            setStageHours('');
            loadHistory();
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || 'Update failed');
        } finally {
            setBusy(false);
        }
    };


    const handleMove = () => {
        if (!target) {
            toast.error('Choose a status to move to');
            return;
        }
        // Approve / Feedback (rework) are the same decision the old
        // dedicated buttons made -- handleDecision's rules (rework loop,
        // required comment/hours) already apply, unchanged.
        if (target === APPROVE_VALUE || target === REJECT_VALUE) {
            handleDecision(target === APPROVE_VALUE ? 'approve' : 'reject');
            return;
        }
        if (target === 'On Hold' && !comment.trim()) {
            toast.error('A comment is mandatory when putting work on hold');
            return;
        }
        if (showQcField && !qcEngineer) {
            toast.error(`Select a QC engineer for ${target.replace('Internal - ', '')}`);
            return;
        }
        let actualHours;
        if (requiresHoursForMove) {
            actualHours = parseHours(stageHours);
            if (actualHours === null) {
                toast.error('Enter the actual hours spent in this stage (e.g. 2 or 02:30)');
                return;
            }
        }
        run(() => onSetStatus({
            status: target, comment: comment.trim() || undefined,
            qc_engineer: showQcField ? (qcEngineer || undefined) : undefined,
            actual_hours: requiresHoursForMove ? actualHours : undefined,
        }), `${label} moved to ${target}`);
    };

    const handleDecision = (decision) => {
        if (decision === 'reject' && !comment.trim()) {
            toast.error('Please say what needs reworking');
            return;
        }
        // Approving into a QC gate (Peer QC -> Final QC, or the stage
        // before Peer QC) needs someone named to actually do that review.
        if (decision === 'approve' && showQcField && !qcEngineer) {
            toast.error(`Select a QC engineer for ${approveTarget.replace('Internal - ', '')}`);
            return;
        }
        // A gate is never "Yet to Start", so hours are always required here.
        const actualHours = parseHours(stageHours);
        if (actualHours === null) {
            toast.error('Enter the actual hours spent in this stage (e.g. 2 or 02:30)');
            return;
        }
        run(() => onDecide({
            decision, comment: comment.trim() || undefined, actual_hours: actualHours,
            qc_engineer: decision === 'approve' && showQcField ? qcEngineer : undefined,
        }), decision === 'approve' ? 'Approved' : 'Sent back for rework');
    };

    return (
        <Box>
            {/* Designer / QC -- always visible at every stage of the workflow.
                Tickets have no QC Engineer (they skip QC entirely). */}
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                    Designer: <strong>{item?.assigned_to_name || 'Unassigned'}</strong>
                </Typography>
                {!isTicket && !simple && (
                    <Typography variant="body2" color="text.secondary">
                        QC: <strong>{item?.qc_engineer_name || 'Unassigned'}</strong>
                    </Typography>
                )}
                {!isTicket && item?.ticket_key && (
                    <Typography variant="body2" color="text.secondary">
                        Linked ticket: <strong>{item.ticket_key}</strong>
                    </Typography>
                )}
            </Stack>

            {/* Current state */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip label={current} color={statusChipColor(current)} />
                {item?.loop_count > 0 && (
                    <Tooltip title="Times this work has come back for rework">
                        <Chip size="small" variant="outlined"
                            label={`${item.loop_count} rework loop${item.loop_count === 1 ? '' : 's'}`} />
                    </Tooltip>
                )}
                {item?.tat_hours != null && (
                    <Chip size="small" variant="outlined" label={`TAT ${fmtTatDays(item.tat_hours)} days`} />
                )}
                {item?.is_overdue && <Chip size="small" color="error" label="Overdue" />}
            </Stack>

            {/* Flow */}
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>
                {mainFlowSteps.map((s) => (
                    <Step key={s} completed={mainFlowSteps.indexOf(s) < activeStep}>
                        <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: 11 } }}>
                            {s.replace('Internal - ', '')}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {current === 'On Hold' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    On hold{item?.hold_reason ? `: ${item.hold_reason}` : ''}. Move it back to
                    In Progress when the blocker clears.
                </Alert>
            )}
            {current === 'Completed' && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                    Completed{item?.completed_at ? ` on ${fmtDate(item.completed_at)}` : ''}.
                </Alert>
            )}
            {current === 'Cancelled' && (
                <Alert severity="info" sx={{ mb: 2 }}>This {label.toLowerCase()} was cancelled.</Alert>
            )}

            {/* Locked: it's not this viewer's turn to act on it right now */}
            {!readOnly && locked && allowed.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Locked — this {label.toLowerCase()} is waiting on
                    {responsibleName ? ` ${responsibleName}` : ` the ${responsible === 'qc' ? 'QC engineer' : 'assigned engineer'}`}.
                    You can't edit it until it comes back to your stage.
                </Alert>
            )}

            {/* Comment box drives both the move and the decision */}
            {!readOnly && allowed.length > 0 && !locked && (
                <>
                    <TextField
                        fullWidth multiline minRows={2} sx={{ mb: 2 }}
                        label={target === 'On Hold' ? 'Comment (required)' : 'Comment (optional)'}
                        placeholder={isGate
                            ? 'Feedback for the engineer, required when rejecting'
                            : 'Add context for this change'}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />

                    {/* Manually entered, added to the running actual_hours total --
                        never asked while still "Yet to Start" (nothing worked on
                        yet), and always asked at a gate decision or any other move. */}
                    {showHoursField && (
                        <TextField
                            fullWidth size="small" required={isGate || requiresHoursForMove} sx={{ mb: 2, maxWidth: { sm: 240 } }}
                            label="Actual Hours" placeholder="00:00"
                            value={stageHours} onChange={(e) => setStageHours(e.target.value)}
                            helperText={`Hours spent in "${current}" -- added to the total`}
                        />
                    )}

                    {/* Move to -- at a gate, Approve/Feedback (rework) sit in this
                        same dropdown instead of separate buttons, so there's one
                        control and one path for every transition. */}
                    <Box>
                        {isGate && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Approve advances to the next stage. Feedback / Rework sends it
                                back to In Progress and counts a loop.
                            </Typography>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField select fullWidth size="small" label="Move to"
                                value={target} onChange={(e) => setTarget(e.target.value)}>
                                {moveOptions.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {value === 'On Hold' && <PauseCircle fontSize="small" sx={{ mr: 1 }} />}
                                        {value === APPROVE_VALUE && <ThumbUp fontSize="small" color="success" sx={{ mr: 1 }} />}
                                        {value === REJECT_VALUE && <ThumbDown fontSize="small" color="error" sx={{ mr: 1 }} />}
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button variant="contained" onClick={handleMove}
                                disabled={busy || !target || (showQcField && !qcEngineer)}
                                startIcon={busy ? <CircularProgress size={16} /> : <ArrowForward />}
                                sx={{ minWidth: 140 }}>
                                Update
                            </Button>
                        </Stack>

                        {/* Only when moving into a QC gate -- pick who reviews it.
                            Open to any team member, not just managers. Required --
                            a QC gate can't be entered unassigned. */}
                        {showQcField && (
                            <TextField select fullWidth size="small" required label="QC Engineer"
                                value={qcEngineer} onChange={(e) => setQcEngineer(e.target.value)}
                                error={!qcEngineer}
                                helperText={qcEngineer
                                    ? 'Who reviews this at Peer / Final QC'
                                    : 'Required to enter Peer / Final QC'}
                                sx={{ mt: 1.5, maxWidth: { sm: 340 } }}>
                                {(ref.engineers || []).map(u => (
                                    <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Box>
                </>
            )}

            {!readOnly && allowed.length === 0 && (
                <Alert severity="info">
                    No further transitions are available from "{current}".
                </Alert>
            )}

            {/* Linked Parts -- tickets only: the parts created off this ticket */}
            {isTicket && linkedParts.length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Linked parts
                    </Typography>
                    <List dense>
                        {linkedParts.map(p => (
                            <ListItem key={p.id} disableGutters>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {p.part_number}
                                            </Typography>
                                            <Chip size="small" label={p.status}
                                                color={statusChipColor(p.status)} />
                                        </Box>
                                    }
                                    secondary={
                                        `${p.part_name}${p.assigned_to_name ? ` · ${p.assigned_to_name}` : ''}`
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </>
            )}

            {/* History */}
            {history.length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History fontSize="small" /> Status history
                    </Typography>
                    <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
                        {[...history].reverse().map(h => (
                            <ListItem key={h.id} disableGutters>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {h.from_status || 'created'}
                                            </Typography>
                                            <ArrowForward sx={{ fontSize: 12 }} />
                                            <Typography variant="body2" fontWeight={600}>
                                                {h.to_status}
                                            </Typography>
                                            {h.action !== 'update' && (
                                                <Chip size="small" label={h.action}
                                                    color={h.action === 'approve' ? 'success' : 'error'}
                                                    sx={{ height: 18, fontSize: 10 }} />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        `${h.changed_by_name || 'system'} · ` +
                                        `${new Date(h.created_at).toLocaleString('en-GB')}` +
                                        `${h.comment ? ` — ${h.comment}` : ''}`
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </>
            )}
        </Box>
    );
};

export default StatusFlowPanel;
